// src/components/app-sidebar.tsx
import React from 'react';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, useSidebar, } from '@/components/ui/sidebar';
import { AlarmClock, Banknote, Building2, Camera, CarFront, ChevronRight, CircleUser, Cog, EllipsisVertical, LayoutDashboard, Link2, LogOut, Megaphone, ShieldCheck, UserCog, } from 'lucide-react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, } from '@/components/ui/dropdown-menu';
import { useAuthenticationDispatch, useAuthenticationState } from '@/hooks/use-authentication';
import { useTranslation } from 'react-i18next';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Logo } from '@/components/logo';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getFullName, getUserInitials } from '@/lib/string';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { signOut } from '@/services/auth';
import { WorkspaceSwitcher } from '@/routes/console/_authenticated/-features/workspace-switcher';
import { isInternalAdmin, isInternalEmployee } from '@/lib/roles';
import { td } from '@/lib/i18n';
const NAV_ITEMS = [
    {
        key: 'dashboard',
        url: '/console',
        icon: LayoutDashboard,
    },
    {
        key: 'campaigns',
        url: '/console/campaigns',
        icon: Megaphone,
    },
    {
        key: 'workspace_settings',
        url: '/console/workspace',
        icon: Building2,
    },
];
// Title comes from the `internal` translation namespace at render time.
const INTERNAL_NAV_ITEMS = [
    {
        key: 'dashboard.title',
        url: '/console/internal',
        icon: LayoutDashboard,
    },
    {
        key: 'drivers.title',
        url: '/console/internal/drivers',
        icon: CarFront,
    },
    {
        key: 'assignments.title',
        url: '/console/internal/assignments',
        icon: Link2,
    },
    {
        key: 'photos.title',
        url: '/console/internal/photos',
        icon: Camera,
    },
    {
        key: 'payouts.title',
        url: '/console/internal/payouts',
        icon: Banknote,
    },
];
const INTERNAL_ADMIN_NAV_ITEMS = [
    {
        key: 'team.title',
        url: '/console/internal/team',
        icon: UserCog,
    },
    {
        key: 'cron.title',
        url: '/console/internal/cron',
        icon: AlarmClock,
    },
    {
        key: 'settings.title',
        url: '/console/internal/settings',
        icon: Cog,
    },
];
export const AppSidebar = (props) => {
    const { workspace, user } = useAuthenticationState();
    const dispatch = useAuthenticationDispatch();
    const { t } = useTranslation();
    const { t: tInternal } = useTranslation('internal');
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { isMobile } = useSidebar();
    const queryClient = useQueryClient();
    const isActive = React.useCallback((url) => {
        if (!url)
            return false;
        // normalize trailing slashes
        const current = pathname.replace(/\/+$/, '');
        const target = url.replace(/\/+$/, '');
        if (current === target)
            return true;
        // Roots that have nested children of their own dashboards must be
        // exact-match only; otherwise they'd light up for every sub-route.
        const exactOnly = ['/console', '/console/internal'];
        return !exactOnly.includes(target) && current.startsWith(target + '/');
    }, [pathname]);
    const renderMenuSub = React.useCallback((items) => items.map((item) => {
        const Icon = item.icon;
        return (<SidebarMenuSubItem key={item.key}>
                        <SidebarMenuSubButton asChild isActive={isActive(item.url)}>
                            <Link to={item.url ?? '/console'}>
                                {/* size-5! overrides the shadcn primitive's
                `[&>svg]:size-4` direct-child selector. */}
                                <Icon className="size-5!"/>
                                <span>{item.title ?? t(item.key)}</span>
                            </Link>
                        </SidebarMenuSubButton>
                    </SidebarMenuSubItem>);
    }), [isActive, t]);
    const renderMenu = React.useCallback((items) => items.map((item) => {
        const Icon = item.icon;
        const label = item.title ?? t(item.key);
        if (item.children && item.children.length > 0) {
            const openByDefault = item.children.some((child) => isActive(child.url));
            return (<Collapsible key={item.key} defaultOpen={openByDefault} className="group/collapsible">
                            <CollapsibleTrigger asChild>
                                <SidebarMenuButton isActive={openByDefault} tooltip={label}>
                                    <Icon className="size-5!"/>
                                    <span>{label}</span>
                                    <ChevronRight className="ml-auto size-4! transition-transform group-data-[state=open]/collapsible:rotate-90"/>
                                </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <SidebarMenuSub>{renderMenuSub(item.children)}</SidebarMenuSub>
                            </CollapsibleContent>
                        </Collapsible>);
        }
        return (<SidebarMenuItem key={item.key}>
                        <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={label}>
                            <Link to={item.url ?? '/console'}>
                                <Icon className="size-5!"/>
                                <span>{label}</span>
                            </Link>
                        </SidebarMenuButton>
                    </SidebarMenuItem>);
    }), [isActive, renderMenuSub, t]);
    const { mutate: doSignOut, isPending: isSigningOut } = useMutation({
        mutationFn: signOut,
        onSuccess: async () => {
            // Keep behavior consistent: clear cache + auth state + redirect
            queryClient.clear();
            dispatch({ type: 'SIGN_OUT' });
            await navigate({ to: '/sign-in' });
            toast.success(t('signed_out_success'));
        },
        onError: () => {
            toast.error(t('signed_out_error'));
        },
    });
    const onSignOut = React.useCallback(() => doSignOut(), [doSignOut]);
    const userEmail = user?.email ?? t('unknown_user');
    const userInitial = getUserInitials(user);
    return (<Sidebar collapsible="icon" {...props}>
            {/* HEADER */}
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground" asChild>
                            <Logo to="/console"/>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                        <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground" asChild>
                            <WorkspaceSwitcher workspace={workspace}/>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>

            {/* CONTENT */}
            <SidebarContent>
                <SidebarGroup>
                    {/* No custom className that could fight shadcn's
            `group-data-[collapsible=icon]:opacity-0` — the
            default already handles fade-out on collapse. */}
                    <SidebarGroupLabel>{t('application')}</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>{renderMenu(NAV_ITEMS)}</SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>

                {isInternalEmployee(user) && (<SidebarGroup>
                        <SidebarGroupLabel className="flex items-center gap-1.5">
                            <ShieldCheck className="h-3 w-3"/>
                            {tInternal('section')}
                        </SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu>
                                {renderMenu(INTERNAL_NAV_ITEMS.map((item) => ({
                ...item,
                title: td(tInternal, item.key),
            })))}
                                {isInternalAdmin(user) &&
                renderMenu(INTERNAL_ADMIN_NAV_ITEMS.map((item) => ({
                    ...item,
                    title: td(tInternal, item.key),
                })))}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>)}
            </SidebarContent>

            {/* FOOTER / USER MENU */}
            <SidebarMenu>
                <SidebarMenuItem>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground" tooltip={userEmail}>
                                <Avatar className="h-8 w-8 rounded-lg grayscale">
                                    <AvatarImage src={user?.avatarUrl} alt={userEmail}/>
                                    <AvatarFallback className="rounded-lg">{userInitial}</AvatarFallback>
                                </Avatar>

                                {/* Hidden in icon mode via the ancestor
            sidebar's `data-collapsible=icon` attr.
            Using CSS (not state) so it works
            reliably inside the dropdown portal. */}
                                <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                                    <span className="truncate font-medium">
                                        {user ? getFullName(user) : t('unknown_user')}
                                    </span>
                                    <span className="truncate text-xs text-muted-foreground">{userEmail}</span>
                                </div>

                                <EllipsisVertical className="ml-auto size-4 group-data-[collapsible=icon]:hidden"/>
                            </SidebarMenuButton>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg" side={isMobile ? 'bottom' : 'right'} align="end" sideOffset={4}>
                            <DropdownMenuLabel className="p-0 font-normal">
                                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                    <Avatar className="h-8 w-8 rounded-lg grayscale">
                                        <AvatarImage src={user?.avatarUrl} alt={userEmail}/>
                                        <AvatarFallback className="rounded-lg">{userInitial}</AvatarFallback>
                                    </Avatar>
                                    <div className="grid flex-1 text-left text-sm leading-tight">
                                        <span className="truncate font-medium">
                                            {user ? getFullName(user) : t('unknown_user')}
                                        </span>
                                        <span className="truncate text-xs text-muted-foreground">{userEmail}</span>
                                    </div>
                                </div>
                            </DropdownMenuLabel>

                            <DropdownMenuSeparator />

                            <DropdownMenuGroup>
                                <DropdownMenuItem asChild>
                                    <Link to="/console/profile">
                                        <CircleUser className="mr-2 h-4 w-4"/>
                                        <span>{t('account')}</span>
                                    </Link>
                                </DropdownMenuItem>

                            </DropdownMenuGroup>

                            <DropdownMenuSeparator />

                            <DropdownMenuItem onClick={onSignOut} disabled={isSigningOut}>
                                <LogOut className="mr-2 h-4 w-4"/>
                                <span>{t('sign_out')}</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </SidebarMenuItem>
            </SidebarMenu>
        </Sidebar>);
};
AppSidebar.displayName = 'AppSidebar';
